// ClientBook (TASK-495) — the filterable book/list widget. Chrome-stripped from
// portfolio/MyBookView: drops SecondaryNav / full-page <main> / floating PortfolioAIChat
// and surfaces the client list with on-tile filter chrome. Reuses useMyBookData +
// MyBookClientList — the route MyBookView is left untouched (AC#4).
//
// Filters: status / minAum / maxAum are forwarded to the backend (server-side narrowing,
// Tier A) via useMyBookData's bookFilters; sortBy is applied client-side here.
// Tier-B keys (mandate/risk/performance/multiPortfolio) are rendered disabled by the bar
// (CLIENTBOOK_FILTERS_PENDING_BACKEND) until their backend plumbing lands.
import { useMemo, useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { useCanvasActions } from '../../../stores/canvasStore';
import { useMyBookData, type MyBookClient, type MyBookServerFilters } from '../../../hooks/useMyBookData';
import { getDefaultPortfolioFilters } from '../../../types/portfolio-views';
import { CLIENTBOOK_FILTERS_PENDING_BACKEND } from '../../../registry/filterContract';
import MyBookClientList from '../../portfolio/MyBookClientList';
import { WidgetContainer } from '../WidgetContainer';
import { WidgetFilterBar } from '../WidgetFilterBar';

type BookFilterState = {
  status?: string[];
  minAum?: number;
  maxAum?: number;
  sortBy?: 'aum_desc' | 'aum_asc' | 'health_asc' | 'name';
  [key: string]: unknown;
};

// Stable cache-key seed for useMyBookData's first arg (book narrowing rides in
// serverFilters, not here) — module-level so it isn't rebuilt per render.
const KEY_SEED = getDefaultPortfolioFilters();

const formatChf = (v: number) =>
  new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF', maximumFractionDigits: 0 }).format(v);

// Status severity for the 'health_asc' sort — backend doesn't emit a per-client health
// score, but status (attention/rebalance/healthy) is the same ordering the book sorts by.
const STATUS_SEVERITY: Record<MyBookClient['status'], number> = {
  no_risk_profile: 0,
  attention: 1,
  rebalance: 2,
  healthy: 3,
};

function sortClients(clients: MyBookClient[], sortBy: BookFilterState['sortBy']): MyBookClient[] {
  if (!sortBy) return clients; // keep backend order (attention-first)
  const sorted = [...clients];
  switch (sortBy) {
    case 'aum_desc':
      return sorted.sort((a, b) => b.totalAuM - a.totalAuM);
    case 'aum_asc':
      return sorted.sort((a, b) => a.totalAuM - b.totalAuM);
    case 'health_asc':
      return sorted.sort((a, b) => STATUS_SEVERITY[a.status] - STATUS_SEVERITY[b.status]);
    case 'name':
      return sorted.sort((a, b) => a.customerName.localeCompare(b.customerName));
    default:
      return sorted;
  }
}

// `openComponent` lets the book double as a picker (SixEase parity): a shortcut/slash
// with no client in focus summons ClientBook with openComponent set, so picking a row
// lands straight in that view. Arrives via spec.props (spread by WidgetRenderer).
export function ClientBook({ filters, openComponent }: { filters?: BookFilterState; openComponent?: string }) {
  const { addSpecs, openClient } = useCanvasActions();
  const [active, setActive] = useState<BookFilterState>(filters ?? {});
  // TASK-603: filter controls collapse behind ⚙ — the widget answers first,
  // filter chrome is opt-in.
  const [showFilters, setShowFilters] = useState(false);

  // Only the server-honoured subset is forwarded; useMyBookData re-fetches on change.
  const serverFilters: MyBookServerFilters = useMemo(
    () => ({ status: active.status, minAum: active.minAum, maxAum: active.maxAum }),
    [active.status, active.minAum, active.maxAum],
  );

  // First arg (PortfolioFilters) only seeds the cache key here — the real book narrowing
  // rides in serverFilters. A stable default keeps the key clean.
  const { data, loading, error } = useMyBookData(KEY_SEED, 'SPI', serverFilters);

  const clients = useMemo(() => sortClients(data?.clients ?? [], active.sortBy), [data, active.sortBy]);

  // SixEase-faithful (ports widgets/ClientBook row click): honor an explicit picker
  // override, else open the client in the RM's default view (openClient → defaultView).
  // Never navigates — ClientBook is canvas-only (the routed page is MyBookView).
  const handleClientClick = (clientId: string) =>
    openComponent
      ? addSpecs([{ component: openComponent, props: { clientId } }])
      : openClient(clientId);

  // TASK-603: per-client top reason from the book's alert feed (drift/compliance/
  // suitability messages the flagging pipeline already computes). Highest severity wins.
  const reasons = useMemo(() => {
    const severityRank = { high: 0, medium: 1, low: 2 } as const;
    const map: Record<string, string> = {};
    const sorted = [...(data?.alerts ?? [])].sort(
      (a, b) => severityRank[a.severity] - severityRank[b.severity],
    );
    for (const alert of sorted) {
      if (alert.clientId && alert.message && !map[alert.clientId]) map[alert.clientId] = alert.message;
    }
    return map;
  }, [data]);

  return (
    <WidgetContainer
      // TASK-871: no caller-supplied title. It was free text over a fixed-enum filter set,
      // so the model headed the FULL roster "Clients with >5% single-issuer concentration"
      // with no issuer column and no filtering of any kind. The filters that ARE applied are
      // visible in the Filters panel below, which is a claim the widget can actually back.
      title="Client Book"
      source="Portfolio API"
      toolbar={
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label={showFilters ? 'Hide filters' : 'Show filters'}
            aria-pressed={showFilters}
            title="Filters"
            onClick={() => setShowFilters((v) => !v)}
            className={`flex h-6 w-6 items-center justify-center rounded-md transition-colors hover:bg-accent ${
              showFilters ? 'text-foreground' : 'text-muted-foreground'
            }`}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
          </button>
          {showFilters && (
            <WidgetFilterBar
              component="ClientBook"
              values={active}
              onChange={(partial) => setActive((prev) => ({ ...prev, ...partial }))}
              disabledKeys={CLIENTBOOK_FILTERS_PENDING_BACKEND}
            />
          )}
        </div>
      }
    >
      {loading && <p className="py-8 text-center text-sm text-muted-foreground">Loading book…</p>}
      {error && <p className="py-8 text-center text-sm text-destructive">{error}</p>}
      {data && (
        <>
          <div className="mb-3 flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{clients.length} clients</span>
            <span className="font-semibold text-foreground">{formatChf(data.summary.totalAuM)}</span>
          </div>
          {clients.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No clients match these filters.</p>
          ) : (
            <MyBookClientList
              clients={clients}
              onClientClick={handleClientClick}
              reasons={reasons}
              onPrepMeeting={(clientId) => addSpecs([{ component: 'MeetingPrep', props: { clientId } }])}
              onShowPortfolio={(clientId) =>
                addSpecs([{ component: 'CockpitPortfolioView', props: { clientId } }])
              }
            />
          )}
        </>
      )}
    </WidgetContainer>
  );
}
